import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SortingRule } from "react-table";
import useSWR from "swr";
import { useAuthContext } from "../../../../../components/Auth";
import { Row } from "../../../../../components/Layout/Layout";
import { SearchBar } from "../../../../../components/SearchBar/SearchBar";
import { Table } from "../../../../../components/Table/Table";
import { H3 } from "../../../../../components/Typography/Typography";
import { WithPermission } from "../../../../../components/WithPermission/WithPermission";
import type { IPagination, WithPagination } from "../../../../../types/types";
import type { AssetsExternalLinks } from "../../../../../types/types.PIM";
import { useDebounce } from "../../../../../util/hooks";
import {
  defaultHandleSort,
  TablePlaceholder,
  useStoreState,
} from "../../../../../util/util";
import { ScrollTableWrapper } from "../DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { ExternalLinkSearchContainer } from "../ExternalLinks/ExternalLinksTable";
import { AssetLink, TableContainer } from "../util";
import { spacingTokens } from "../../../../../designTokens";
import { PROFILE_DETAIL_CLASSES } from "../../../../../layout/shared/ProfileDetailLayout";

const LIMIT = 20;

export const ExternalLinksPortfolioAssets = ({
  product_id,
}: {
  product_id: string;
}) => {
  const { t } = useTranslation();
  const {
    storefront_metadata: { enable_product_asset_search },
    tenant_id,
  } = useStoreState();
  const { hasPermission, roleIsSomeKindOfSeller } = useAuthContext();

  const [searchQuery, setSearchQuery] = useState("");
  const [tableData, setTableData] = useState<{}[]>([]);
  const [linksData, setLinksData] = useState<AssetsExternalLinks[]>([]);
  const [pagination, setPagination] = useState<IPagination>();
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [offset, setOffset] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });

  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    data: external_links,
    error,
    isValidating,
  } = useSWR<WithPagination<{ data: AssetsExternalLinks[] }>>([
    `/v2/tenants/${tenant_id}/pim/products/${product_id}/external-links`,
    useMemo(
      () => ({
        params: {
          ...(debouncedSearchQuery ? { q: debouncedSearchQuery } : {}),
          offset,
          limit: LIMIT,
        },
      }),
      [debouncedSearchQuery, offset]
    ),
  ]);

  const tableColumns = useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
        cell: ({
          getValue,
          row: {
            original: { url, is_visible },
          },
        }: {
          getValue: () => string;
          row: {
            original: {
              url: string;
              is_visible: boolean;
            };
          };
        }) => (
          <AssetLink
            url={url}
            name={getValue() as string}
            is_visible={is_visible}
          />
        ),
      },
    ],
    [t]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };
  const handleClearSearch = () => {
    setSearchQuery("");
    setOffset(0);
  };

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollTop = scrollRef.current.scrollTop;
      if (
        scrollRef.current.offsetHeight - scrollTop < 400 &&
        !isLoadingData &&
        ((pagination?.total && linksData.length < pagination.total) ||
          !pagination) &&
        !isValidating
      ) {
        setIsLoadingData(true);
        setOffset(offset + LIMIT);
      }
    }
  };

  useEffect(() => {
    setTableData(
      linksData.reduce<
        { id: string; url: string; name: string; is_visible: boolean }[]
      >((acc, { id, url, name, is_visible }) => {
        acc.push({
          id,
          url,
          is_visible,
          name: name ?? "--",
        });
        return acc;
      }, [])
    );
  }, [linksData]);

  useEffect(() => {
    if (external_links?.data) {
      if (offset === 0) {
        setLinksData(external_links.data);
      } else {
        setLinksData((prev) => [...prev, ...external_links.data]);
      }
      setIsLoadingData(false);
      setPagination(external_links.pagination);
    }
  }, [external_links, offset]);

  const is_loading = !external_links && !error;

  const is_search_enabled =
    enable_product_asset_search &&
    (!roleIsSomeKindOfSeller ||
      hasPermission(["view_assets", "view_products"], "every"));

  return tableData.length === 0 &&
    (!is_search_enabled || !debouncedSearchQuery) ? (
    <></>
  ) : (
    <div className={PROFILE_DETAIL_CLASSES.detailsSection}>
      <H3 style={{ marginTop: spacingTokens.spacing_none }}>
        {t("External Links")}
      </H3>
      {enable_product_asset_search && (
        <>
          {roleIsSomeKindOfSeller ? (
            <WithPermission
              permission={["view_assets", "view_products"]}
              permission_type="every"
            >
              <ExternalLinkSearchContainer>
                <Row style={{ margin: `${spacingTokens.spacing_lg} 0` }}>
                  <SearchBar
                    query={searchQuery}
                    placeHolder={t("Search by Link Name or URL")}
                    handleChange={handleSearch}
                    handleClearInput={handleClearSearch}
                    maxWidth="450px"
                  />
                </Row>
              </ExternalLinkSearchContainer>
            </WithPermission>
          ) : (
            <ExternalLinkSearchContainer>
              <Row style={{ margin: `${spacingTokens.spacing_lg} 0` }}>
                <SearchBar
                  query={searchQuery}
                  placeHolder={t("Search by Link Name or URL")}
                  handleChange={handleSearch}
                  handleClearInput={handleClearSearch}
                  maxWidth="450px"
                />
              </Row>
            </ExternalLinkSearchContainer>
          )}
        </>
      )}
      <TableContainer
        style={{ paddingBottom: spacingTokens.spacing_5xl }}
        lastChild
      >
        <ScrollTableWrapper ref={scrollRef} onScroll={handleScroll}>
          <Table
            columns={tableColumns}
            data={tableData}
            isLoading={is_loading}
            handleSort={handleSort}
            error={undefined}
            lastChildleftAlign
            Placeholder={<TablePlaceholder />}
          />
        </ScrollTableWrapper>
      </TableContainer>
    </div>
  );
};
